<template>
  <div class="toolbox-menu">
    <ToolboxDrawers
      ref="toolboxDrawers"
      :drawers="toolboxDrawers"
      :selected-drawer="selectedDrawer"
      @selected-tool="setSelectedTool"
      @set-selected-drawer="selectedDrawer = $event"
    />
    <component
      :is="selectedTool?.component"
      v-show="selectedTool"
      class="toolbox-menu__selected-tool"
    />
    <ToolboxButtons
      :buttons="shownButtons"
      @buttonClicked="handleButtonClicked"
    />
  </div>
</template>

<script>
// Packages
import {mapMutations, mapState, mapActions} from 'vuex';

// Services
import {OverlayService} from '@/services/OverlaysService';
import {currentUser} from '@/services/FirestoreService';
import {MapService} from '@/services/MapService';

// Components
import ToolboxDrawers from './sections/ToolboxDrawers.vue';
import ToolboxButtons from './sections/ToolboxButtons.vue';

/**
 * @typedef {import('@/internal/store/toolbox').ToolboxDrawer} ToolboxDrawer
 * @typedef {import('@/internal/store/toolbox').ToolboxTool} ToolboxTool
 */

export default {
  name: 'ToolboxMenu',
  components: {
    ToolboxDrawers,
    ToolboxButtons,
  },
  data() {
    return {
      toolboxButtons: [{
        name: 'Clear All',
        handler: this.clearAll,
        variant: 'outlined',
      }],
      /** @type {ToolboxTool} */
      selectedTool: null,
      /** @type {ToolboxDrawer} */
      selectedDrawer: null,
      /** @type {() => void} */
      cancelCallback: null,
    };
  },
  computed: {
    ...mapState('toolbox', ['toolboxOverlay', 'toolboxDrawers']),
    ...mapState(['mapId']),

    shownButtons() {
      return this.toolboxButtons.concat(this.selectedTool?.buttons ?? []);
    },
  },
  async mounted() {
    if (this.toolboxOverlay) return;

    const overlay = await OverlayService.newOverlay({
      name: 'Toolbox Overlay',
      private: true,
      defaultVisibility: true,
      createdByUid: this.mapId ? `shared-${this.mapId}` : currentUser().uid,
    });
    this.setToolboxOverlay(overlay);
    this.toolboxOverlay.setRender(true);
    // When the map zoom changes, disable any tools that are no longer usable.
    // Also perform the check initially.
    MapService.addMapListener('toolboxZoomHandler', 'zoom_changed', this.handleZoomChanged);
    MapService.addMapListener('toolboxMapClickHandler', 'click', this.handleMapClick);
    this.handleZoomChanged();
  },
  deactivated() {
    this.setSelectedTool(null);
    this.$refs.toolboxDrawers.closeOpenDrawer();
  },
  methods: {
    ...mapMutations('toolbox', ['setToolboxOverlay']),
    ...mapActions('infowindow', ['closeInfoWindow']),

    /** @param {ToolboxTool} tool */
    setSelectedTool(tool) {
      const oldTool = this.selectedTool;
      if (oldTool?.onDeselect) {
        oldTool.onDeselect();
      }

      this.selectedTool = tool;
      if (this.selectedTool?.onSelect) {
        if (this.selectedTool?.handleAsFeature) {
          this.selectedTool.onSelect(async (featureData) => {
            this.handleAddFeature(featureData);
            this.setSelectedTool(null);
            this.selectedDrawer = null;
          });
        } else {
          this.selectedTool.onSelect();
        }
      }
    },
    /**
     * Handles a button being clicked
     * @param {string} name The name of the clicked button
     */
    handleButtonClicked(name) {
      const handler = this.shownButtons?.find((button) => button.name === name)?.handler;
      if (handler && typeof handler === 'function') {
        handler();
      }
    },
    /**
     * Handles the creation of a new feature in the Toolbox overlay.
     * @param {object} featureData The data for the new feature.
     */
    async handleAddFeature(featureData) {
      const minZoom = this.selectedTool?.minZoom ?? 0;
      this.closeInfoWindow();
      this.setSelectedTool(null);
      if (!featureData) return;
      featureData.toolBoxMinZoom = minZoom;
      const newFeature = await this.toolboxOverlay.newFeature(featureData);
      if (newFeature.featureType !== 'label') {
        newFeature.openInfoWindow({});
      }
    },
    handleZoomChanged() {
      const zoom = MapService.getMap().getZoom();
      this.toolboxDrawers.forEach((drawer) => {
        if ('minZoom' in drawer && zoom < drawer.minZoom) {
          drawer.disabled = true;
          if (this.selectedDrawer === drawer) {
            this.setSelectedTool(null);
          }
        } else {
          drawer.disabled = false;
        }
      });
    },
    handleMapClick() {
      for (const feature of this.toolboxOverlay.features.values()) {
        feature.setEditing(false);
      }
    },
    /**
     * Clears all Toolbox tools and features.
     */
    async clearAll() {
      const tools = this.toolboxDrawers.flatMap((drawer) => drawer.tools);
      tools.forEach((tool) => {
        const clear = tool.onClear;
        if (typeof clear === 'function') {
          clear(tool === this.selectedTool);
        }
      });

      const keys = this.toolboxOverlay?.features.keys() ?? null;
      if (keys) {
        await Promise.all([...keys]?.map((id) => this.toolboxOverlay?.deleteFeature(id)));
      }

      this.$refs.toolboxDrawers.closeOpenDrawer();
    },
  },
};
</script>

<style lang="scss" scoped>
.toolbox-menu {
  max-width: max-content;
  height: auto;

  @include main.respond(desktop) {
    width: max-content;
  }

  &__selected-tool {
    padding: 20px 12px;
    min-height: 90px;
    max-width: 315px;
    border-radius: 5px;
    margin-bottom: -2px;
    background-color: #FFF;
  }
}
</style>
